import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Crosshair, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function DeptSettingsCard({ dept, existing }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    latitude: "", longitude: "", radius_m: 200, punctual_by: "09:00", late_after: "09:30",
    early_departure_before: "16:30", beyond_time: "11:00", weekly_offs: [6],
    absent_if_out_before: "", halfday_in_after: "", halfday_out_before: "",
    attendance_threshold: 70, daily_report_required: true,
  });

  useEffect(() => {
    if (existing) setForm({
      latitude: existing.latitude, longitude: existing.longitude, radius_m: existing.radius_m,
      punctual_by: existing.punctual_by || "09:00",
      late_after: existing.late_after, early_departure_before: existing.early_departure_before,
      beyond_time: existing.beyond_time || "11:00", weekly_offs: existing.weekly_offs || [6],
      absent_if_out_before: existing.absent_if_out_before || "",
      halfday_in_after: existing.halfday_in_after || "", halfday_out_before: existing.halfday_out_before || "",
      attendance_threshold: existing.attendance_threshold ?? 70,
      daily_report_required: existing.daily_report_required ?? true,
    });
  }, [existing]);

  const useMyLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, latitude: +pos.coords.latitude.toFixed(6), longitude: +pos.coords.longitude.toFixed(6) })),
      () => toast.error("Location unavailable — enter coordinates manually"),
      { enableHighAccuracy: true }
    );
  };

  const toggleDay = (i) => {
    setForm((f) => ({
      ...f,
      weekly_offs: f.weekly_offs.includes(i) ? f.weekly_offs.filter((d) => d !== i) : [...f.weekly_offs, i],
    }));
  };

  const save = async () => {
    try {
      await api.put("/staff/admin/settings", {
        department_id: dept.id,
        latitude: Number(form.latitude), longitude: Number(form.longitude),
        radius_m: Number(form.radius_m), punctual_by: form.punctual_by, late_after: form.late_after,
        early_departure_before: form.early_departure_before, beyond_time: form.beyond_time,
        weekly_offs: form.weekly_offs,
        absent_if_out_before: form.absent_if_out_before || null,
        halfday_in_after: form.halfday_in_after || null, halfday_out_before: form.halfday_out_before || null,
        attendance_threshold: Number(form.attendance_threshold),
        daily_report_required: !!form.daily_report_required,
      });
      toast.success(`Settings saved for ${dept.name}`);
      queryClient.invalidateQueries({ queryKey: ["staff-settings"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid={`settings-card-${dept.id}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold tracking-tight text-slate-950">{dept.name} <span className="text-slate-400 font-data text-xs">{dept.code}</span></h3>
        {existing && <span className="text-xs text-emerald-700">Configured</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label>Latitude</Label>
          <div className="flex gap-2">
            <Input type="number" step="any" data-testid={`geo-lat-${dept.id}`} value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
            <Button variant="outline" size="icon" onClick={useMyLocation} data-testid={`geo-locate-${dept.id}`} title="Use my location">
              <Crosshair className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Longitude</Label>
          <Input type="number" step="any" data-testid={`geo-lng-${dept.id}`} value={form.longitude}
            onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Radius (meters)</Label>
          <Input type="number" min="10" data-testid={`geo-radius-${dept.id}`} value={form.radius_m}
            onChange={(e) => setForm({ ...form, radius_m: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Punctual if clock-in by</Label>
          <Input type="time" data-testid={`punctual-by-${dept.id}`} value={form.punctual_by}
            onChange={(e) => setForm({ ...form, punctual_by: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Late if clock-in after</Label>
          <Input type="time" data-testid={`late-after-${dept.id}`} value={form.late_after}
            onChange={(e) => setForm({ ...form, late_after: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Early departure if clock-out before</Label>
          <Input type="time" data-testid={`early-before-${dept.id}`} value={form.early_departure_before}
            onChange={(e) => setForm({ ...form, early_departure_before: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Beyond time (clock-in after this counts as absent)</Label>
          <Input type="time" data-testid={`beyond-time-${dept.id}`} value={form.beyond_time}
            onChange={(e) => setForm({ ...form, beyond_time: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Before time (clock-out before this time counts as absent)</Label>
          <Input type="time" data-testid={`absent-out-before-${dept.id}`} value={form.absent_if_out_before}
            onChange={(e) => setForm({ ...form, absent_if_out_before: e.target.value })} />
          <p className="text-xs text-slate-400">Clock-out before this time = Absent. Leave empty to disable.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Late clock-in threshold</Label>
          <Input type="time" data-testid={`halfday-in-after-${dept.id}`} value={form.halfday_in_after}
            onChange={(e) => setForm({ ...form, halfday_in_after: e.target.value })} />
          <p className="text-xs text-slate-400">Clock-in after this time = Half Day Leave — auto-deducted 0.5 from Casual/Medical Leave unless an approved half-day application exists. Leave empty to disable.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Early clock-out threshold</Label>
          <Input type="time" data-testid={`halfday-out-before-${dept.id}`} value={form.halfday_out_before}
            onChange={(e) => setForm({ ...form, halfday_out_before: e.target.value })} />
          <p className="text-xs text-slate-400">Clock-out before this time = Half Day Leave — same auto-deduction applies. Leave empty to disable.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Weekly off days</Label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d, i) => (
              <button key={d} type="button" data-testid={`weekoff-${dept.id}-${i}`} onClick={() => toggleDay(i)}
                className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors duration-150 ${
                  form.weekly_offs.includes(i)
                    ? "bg-slate-950 text-white border-slate-950"
                    : "bg-white text-slate-500 border-slate-300 hover:border-slate-950 hover:text-slate-950"}`}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Student attendance threshold (%)</Label>
          <Input type="number" min="0" max="100" step="1" data-testid={`attendance-threshold-${dept.id}`}
            value={form.attendance_threshold}
            onChange={(e) => setForm({ ...form, attendance_threshold: e.target.value })} />
          <p className="text-xs text-slate-400">Students below this overall % get daily warnings.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Daily report required</Label>
          <div className="flex items-center gap-3 pt-1">
            <Switch checked={!!form.daily_report_required} data-testid={`daily-report-toggle-${dept.id}`}
              onCheckedChange={(v) => setForm({ ...form, daily_report_required: v })} />
            <span className="text-sm text-slate-500">Teachers must submit a daily report before clock-out (4pm/5pm reminders)</span>
          </div>
        </div>
      </div>
      <Button onClick={save} data-testid={`geo-save-${dept.id}`} className="mt-4 bg-slate-950 hover:bg-slate-800 text-white" size="sm">
        Save settings
      </Button>
    </div>
  );
}

export default function StaffSettings() {
  const queryClient = useQueryClient();
  const [holiday, setHoliday] = useState({ date: "", name: "", department_id: "all" });

  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/admin/departments")).data });
  const { data: settings = [] } = useQuery({ queryKey: ["staff-settings"], queryFn: async () => (await api.get("/staff/admin/settings")).data });
  const { data: holidays = [] } = useQuery({ queryKey: ["holidays"], queryFn: async () => (await api.get("/staff/admin/holidays")).data });

  const addHoliday = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/admin/holidays", {
        date: holiday.date, name: holiday.name,
        department_id: holiday.department_id === "all" ? null : holiday.department_id,
      });
      toast.success("Holiday added");
      setHoliday({ date: "", name: "", department_id: "all" });
      queryClient.invalidateQueries({ queryKey: ["holidays"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const removeHoliday = async (id) => {
    await api.delete(`/staff/admin/holidays/${id}`);
    toast.success("Holiday removed");
    queryClient.invalidateQueries({ queryKey: ["holidays"] });
  };

  const deptName = (id) => (id ? departments.find((d) => d.id === id)?.name || "—" : "All departments");

  return (
    <Layout title="Staff Attendance Settings">
      <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950 mb-4">Geofence & work rules (per department)</h2>
      <div className="space-y-6 mb-10">
        {departments.length === 0 && (
          <p className="text-sm text-slate-400" data-testid="settings-no-depts">Create a department first (Structure page).</p>
        )}
        {departments.map((d) => (
          <DeptSettingsCard key={d.id} dept={d} existing={settings.find((s) => s.department_id === d.id)} />
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="holidays-card">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Holidays</h2>
        </div>
        <form onSubmit={addHoliday} className="px-6 py-4 border-b border-slate-200 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" required data-testid="holiday-date-input" value={holiday.date}
              onChange={(e) => setHoliday({ ...holiday, date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Holiday name</Label>
            <Input required data-testid="holiday-name-input" value={holiday.name}
              onChange={(e) => setHoliday({ ...holiday, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Applies to</Label>
            <Select value={holiday.department_id} onValueChange={(v) => setHoliday({ ...holiday, department_id: v })}>
              <SelectTrigger data-testid="holiday-dept-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="holiday-add-btn" className="bg-slate-950 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4" /> Add holiday
          </Button>
        </form>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Applies to</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {holidays.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8" data-testid="holidays-empty">
                No holidays configured.
              </TableCell></TableRow>
            )}
            {holidays.map((h) => (
              <TableRow key={h.id} data-testid={`holiday-row-${h.id}`}>
                <TableCell className="font-data">{h.date}</TableCell>
                <TableCell>{h.name}</TableCell>
                <TableCell className="text-slate-500">{deptName(h.department_id)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" data-testid={`delete-holiday-${h.id}`} onClick={() => removeHoliday(h.id)}
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Layout>
  );
}
